/**
 * Dragging an image from one slot to another (日本-100).
 *
 * Slots live in three places — a sentence in the reading log, the add-card panel, the Cards note
 * dock — and the same gesture has to mean the same thing in all of them. This is that rule, kept
 * pure so `npm test` can reach it without a DOM.
 *
 * **The two rules the todo states, generalised to every pair:**
 *
 * | target | result |
 * |---|---|
 * | empty  | **copy** — the source keeps its image too ("the image should now reside on both") |
 * | filled | **swap** — the two exchange ("the images should be flipped") |
 *
 * **Applied whether or not the two slots belong to the same owner, which goes beyond what the todo
 * says.** It states both rules only for the same-sentence case and is silent on
 * sentence→sentence. Uniform is the safe generalisation because every outcome is **lossless** and
 * dragging back undoes it, whereas the obvious alternative for a cross-owner filled target — let
 * the drop overwrite — destroys an image with no undo and no confirmation. Slot values are just
 * field strings, so a copy duplicates no bytes and costs nothing.
 *
 * **Drops are same-KIND only: sentence→sentence and note→note.** Not a limitation of this function,
 * which plans any pair — the callers refuse the mixed case. A sentence is session state written
 * synchronously; a note is a row written over HTTP. A swap across that boundary is two writes of
 * different kinds where the server half can fail after the local half has already repainted, which
 * is the one outcome worth engineering against. The todo asks for "between sentences" and "between
 * the two image fields", and both of those are same-kind.
 */

import type { SlotKey } from "./ImageSlots";

/**
 * Who owns a slot.
 *
 * The two id types are genuinely different and are left that way. A **sentence** id is a UUID this
 * client minted (日本-207); a **note** id is Anki's own integer. Widening both to `string | number`
 * to make the union tidier would let a caller build a `{kind: "note", id: "abc"}` that type-checks
 * and 404s.
 */
export type SlotOwner =
  | { kind: "sentence"; id: string }
  | { kind: "note"; id: number };

/** One end of a drag: which slot, and what is in it. */
export interface SlotRef {
  owner: SlotOwner;
  slot: SlotKey;
  /** The Anki field value, e.g. `<img src="vn-1.png">`. Empty means the slot is free. */
  value: string;
  filename: string | null;
}

/**
 * What a drop should do.
 *
 * `assign` is a flat list on purpose: a swap is two assignments, a copy is one, and the executor
 * then has a single loop instead of a branch per move kind. It is also what makes the ordering rule
 * expressible — see `NoteDock`, which applies server-backed assignments before local ones.
 */
export type SlotMove =
  | { kind: "noop" }
  | { kind: "copy"; assign: SlotAssignment[] }
  | { kind: "swap"; assign: SlotAssignment[] };

export interface SlotAssignment {
  owner: SlotOwner;
  slot: SlotKey;
  /** The value to write. Empty string clears the slot. What a *sentence* slot stores. */
  value: string;
  /**
   * The file `value` points at. What a *note* slot needs, since `linkMedia` takes a name rather
   * than a field value.
   *
   * **Never null**, and that falls out of the rules rather than being enforced: a copy requires a
   * filled source, and a swap requires both sides filled, so every assignment any plan emits
   * writes a real file. Nothing here ever empties a slot.
   *
   * **Carried rather than re-derived, and that is a correctness rule, not a convenience.** It
   * arrives from the server's own `MediaRef.filename`; re-deriving it here means a *second* parser
   * has to agree with that one about every field in the collection, and where it disagreed by
   * returning null a swap's second write became a clear that **deleted the image it was supposed to
   * be moving**. Silent, and destructive.
   *
   * The two parsers agreed as of the unquoted-`src` fix — both now take double-quoted,
   * single-quoted and bare — so the specific divergence that motivated this (single quotes, which
   * the server accepted and the client did not) no longer exists. **The rule stays anyway**, and
   * the fact that the divergence was real once and then grew a second instance is the argument: a
   * bare `src` was invisible to *both* for far longer. Carrying the value cannot drift; agreeing
   * about a regex is a thing to keep being right about.
   */
  filename: string;
}

function sameSlot(a: SlotRef, b: SlotRef): boolean {
  return a.owner.kind === b.owner.kind && sameId(a.owner.id, b.owner.id) && a.slot === b.slot;
}

/**
 * Compare two owner ids across the union.
 *
 * `a.owner.id === b.owner.id` does not type-check once the two kinds carry different id types, and
 * the two obvious escapes are both worse: casting hides a real mismatch, and stringifying would
 * make note `1` equal sentence `"1"` — which the `kind` check above already prevents, but only by
 * accident of ordering. This is a widening, not a cast: both sides really are `string | number`.
 */
function sameId(a: string | number, b: string | number): boolean {
  return a === b;
}

/** Whether a slot holds an image. Keyed on the filename, like `ImageSlots`' own `filled`. */
function filled(ref: SlotRef): boolean {
  return ref.filename !== null && ref.value !== "";
}

export function planSlotMove(from: SlotRef, to: SlotRef): SlotMove {
  // Dropping a slot on itself, and dragging an empty one, are both nothing. Neither should be
  // reachable through the UI — an empty slot is not draggable — but a plan that is wrong about
  // them would be wrong by writing, so they are refused here rather than guarded at each caller.
  if (sameSlot(from, to)) return { kind: "noop" };
  if (!filled(from)) return { kind: "noop" };

  // `filled` has already established that these are non-null; the assertions are what let
  // `SlotAssignment.filename` be a plain string, so no consumer has to handle a case the rules
  // cannot produce.
  if (!filled(to)) {
    return {
      kind: "copy",
      assign: [{ owner: to.owner, slot: to.slot, value: from.value, filename: from.filename! }],
    };
  }

  return {
    kind: "swap",
    assign: [
      { owner: to.owner, slot: to.slot, value: from.value, filename: from.filename! },
      { owner: from.owner, slot: from.slot, value: to.value, filename: to.filename! },
    ],
  };
}
